import { useState } from 'react';
import { toast } from 'react-toastify';

import fleetApi from '../../services/api.service';

/**
 * Disponibilité d'un véhicule (véhicules du jour).
 *
 * Même hook que sur tms-chargeur (src/api/vehicles/useVehicleAvailability.js) :
 * - l'indisponibilité datée, posée ou levée jour par jour (PUT et DELETE
 *   /vehicles/:key/unavailabilities) ;
 * - l'indicateur général de la fiche véhicule (PUT /vehicles/:key/availability).
 *
 * Aucune ne retire les commandes que le camion porte : la réponse les compte
 * (affected_assignments), c'est à l'appelant de le dire. Un refus est affiché
 * ici ; l'appelant reçoit null.
 *
 * @returns {object} - { isSaving, setUnavailabilities, removeUnavailability, setAvailability }
 */
export default function useVehicleAvailability() {
  const [isSaving, setIsSaving] = useState(false);

  const run = async (request, failure) => {
    setIsSaving(true);

    try {
      const response = await request();

      return response.data;
    } catch (error) {
      toast.error(error?.response?.data?.message || failure);

      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const path = (vehiculeKey) => `/vehicles/${encodeURIComponent(vehiculeKey)}`;

  /**
   * @param {string} vehiculeKey - Le camion
   * @param {Array<string>} dates - Les jours (YYYY-MM-DD)
   * @param {string|null} [reason] - Le motif, facultatif
   * @returns {Promise<object|null>} - { vehicule_key, dates, reason, affected_assignments }
   */
  const setUnavailabilities = (vehiculeKey, dates, reason = null) =>
    run(
      () =>
        fleetApi.put(`${path(vehiculeKey)}/unavailabilities`, {
          dates,
          reason: reason || null,
        }),
      "L'indisponibilité n'a pas été posée, veuillez réessayer."
    );

  /**
   * @param {string} vehiculeKey - Le camion
   * @param {string} date - Le jour à lever (YYYY-MM-DD)
   * @returns {Promise<object|null>} - { vehicule_key, date, removed }
   */
  const removeUnavailability = (vehiculeKey, date) =>
    run(
      () =>
        fleetApi.delete(
          `${path(vehiculeKey)}/unavailabilities?${new URLSearchParams({ date }).toString()}`
        ),
      "L'indisponibilité n'a pas été levée, veuillez réessayer."
    );

  /**
   * @param {string} vehiculeKey - Le camion
   * @param {boolean} isAvailable - Disponible pour l'affectation
   * @returns {Promise<object|null>} - { vehicule_key, is_available_for_optimization, affected_assignments }
   */
  const setAvailability = (vehiculeKey, isAvailable) =>
    run(
      () =>
        fleetApi.put(`${path(vehiculeKey)}/availability`, {
          is_available_for_optimization: isAvailable,
        }),
      "La disponibilité du véhicule n'a pas été modifiée, veuillez réessayer."
    );

  return {
    isSaving,
    setUnavailabilities,
    removeUnavailability,
    setAvailability,
  };
}
